import "./editBlog.css";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { Editor } from "react-draft-wysiwyg";
import { ContentState, convertToRaw, EditorState } from "draft-js";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import blogApi from "../../apis/blogApi";
import {
  Alert,
  Button,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { Label } from "semantic-ui-react";
import draftToHtml from "draftjs-to-html";
import DOMPurify from "dompurify";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import htmlToDraft from "html-to-draftjs";
import DefaultLayout from "../layout/DefaultLayout";
import S3 from "react-aws-s3";

const EditBlog = () => {
  const location = useLocation();
  const path = location.pathname.split("/")[3];
  const navigate = useNavigate();

  const [blog, setBlog] = useState({});
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [authorName, setAuthorName] = useState("");
  const [authorLink, setAuthorLink] = useState("");
  const [editorLink, setEditorLink] = useState("");
  const [editedBy, setEditedBy] = useState("");
  const [content, setContent] = useState("");
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const { user } = useSelector((state) => state.user);
  const authorId = user?._id;

  const config = {
    bucketName: process.env.REACT_APP_S3_BUCKET_NAME,
    region: process.env.REACT_APP_S3_REGION,
    accessKeyId: process.env.REACT_APP_AWS_AKID,
    secretAccessKey: process.env.REACT_APP_AWS_SAK,
  };

  useEffect(() => {
    getBlog();
  }, [path]);

  const getBlog = async () => {
    try {
      const res = await blogApi.getABlog(path);
      if (res) {
        setBlog(res);
        setTitle(res.title);
        setAuthorName(res.authorName);
        setEditedBy(res.editedBy);
        setAuthorLink(res.authorLink);
        setEditorLink(res.editorLink);

        // Convert HTML to DraftJS
        const contentBlock = htmlToDraft(res.content);
        const contentState = ContentState.createFromBlockArray(
          contentBlock.contentBlocks
        );
        setEditorState(EditorState.createWithContent(contentState));
        setContent(res.content);
      } else {
        setError(`Unable to retrieve blog ${path}`);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch blog data.");
    }
  };

  const uploadImage = async (image) => {
    try {
      const ReactS3Client = new S3(config);
      const upload = await ReactS3Client.uploadFile(
        image,
        Date.now() + image.name
      );
      return Promise.resolve({ data: { link: upload.location } });
    } catch (err) {
      console.error(err);
      return Promise.reject(err);
    }
  };

  const editBlog = async () => {
    if (!title || !content || !authorName) {
      setError("Please fill out all required fields!");
      return;
    }
    setError("");
    setSaving(true);

    try {
      let imageUrl = blog.image; // default to existing image
      if (file) {
        const ReactS3Client = new S3(config);
        const upload = await ReactS3Client.uploadFile(
          file,
          Date.now() + file.name
        );
        imageUrl = upload.location;
      }

      const updatedBlog = {
        title,
        content,
        authorName,
        authorLink,
        editorLink,
        authorId,
        image: imageUrl,
        editedBy,
      };

      const res = await blogApi.editABlog(path, updatedBlog);
      if (res) {
        toast.success("Blog saved successfully!");
        navigate(`/blog/${path}`);
      } else {
        setError("Blog update failed!");
      }
    } catch (err) {
      console.error(err);
      setError("An error occurred while updating the blog.");
    } finally {
      setSaving(false);
    }
  };

  const createMarkup = (html) => ({
    __html: DOMPurify.sanitize(html),
  });

  return (
    <DefaultLayout>
      <div className="blog-editor">
        <header className="blog-header">
          <button
            onClick={() => navigate(`/blog/${path}`)}
            className="blog-button"
          >
            &lt; Back to blog
          </button>
          <span>Edit Blog</span>
        </header>

        {error && (
          <Alert variant="danger" className="mt-4">
            {error}
          </Alert>
        )}

        <Container fluid className="d-flex">
          <Col md={6} className="p-3">
            <Form className="blog-form">
              <Form.Group className="mb-3">
                <Label className="mb-2">Title *</Label>
                <Form.Control
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Label className="mb-2">Image</Label>
                <Form.Control
                  type="file"
                  onChange={(e) => setFile(e.target.files[0])}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Row>
                  <Col md={6}>
                    <Label className="mb-2">Author</Label>
                    <Form.Control
                      type="text"
                      value={authorName}
                      onChange={(e) => setAuthorName(e.target.value)}
                    />
                  </Col>
                  <Col md={6}>
                    <Label className="mb-2">Social Link</Label>
                    <Form.Control
                      type="text"
                      value={authorLink}
                      onChange={(e) => setAuthorLink(e.target.value)}
                    />
                  </Col>
                </Row>
              </Form.Group>

              <Form.Group className="mb-3">
                <Row>
                  <Col md={6}>
                    <Label className="mb-2">Editor</Label>
                    <Form.Control
                      type="text"
                      value={editedBy}
                      onChange={(e) => setEditedBy(e.target.value)}
                    />
                  </Col>
                  <Col md={6}>
                    <Label className="mb-2">Social Link</Label>
                    <Form.Control
                      type="text"
                      value={editorLink}
                      onChange={(e) => setEditorLink(e.target.value)}
                    />
                  </Col>
                </Row>
              </Form.Group>

              <Form.Group className="mb-3">
                <Label className="mb-2">Content</Label>
                <Editor
                  editorState={editorState}
                  onEditorStateChange={(newState) => {
                    setEditorState(newState);
                    setContent(
                      draftToHtml(convertToRaw(newState.getCurrentContent()))
                    );
                  }}
                  toolbar={{
                    inline: { inDropdown: true },
                    list: { inDropdown: true },
                    textAlign: { inDropdown: true },
                    link: { inDropdown: true },
                    history: { inDropdown: true },
                    image: {
                      uploadCallback: uploadImage,
                      inputAccept: "image/*",
                      alignmentEnabled: true,
                      defaultSize: { height: "100%", width: "100%" },
                      className: "editor-image",
                    },
                  }}
                  wrapperClassName="card"
                  editorClassName="card-body"
                  toolbarClassName="toolbar-class"
                />
              </Form.Group>

              <Form.Group className="button-loader">
                <Button className="btn btn-warning" onClick={editBlog}>
                  Save <i className="fas fa-save ms-2"></i>
                </Button>
                {saving && <Spinner animation="border" />}
              </Form.Group>
            </Form>
          </Col>

          <Col md={6} className="p-3">
            <div className="blog-preview">
              <Label className="text-center fs-5">P R E V I E W</Label>
              <div className="border p-2 mt-2">
                <div dangerouslySetInnerHTML={createMarkup(content)}></div>
              </div>
            </div>
          </Col>
        </Container>
      </div>
    </DefaultLayout>
  );
};

export default EditBlog;
